import { useEffect } from 'react'
import ContentLoader from 'react-content-loader'
import { Link } from 'react-router-dom'
import styled, { useTheme } from 'styled-components'

import { useVaultInfo } from '@/shared/kai/hooks/useVaultInfo'
import { round, USDC } from '@kunalabs-io/kai'
import { IconButtonNext } from 'components/Icons/IconButtonNext'
import { Typography } from 'components/Typography/Typography'
import { useIsMobile } from 'utils/useIsMobile'
import { useUserVaultInfo } from 'utils/useUserVaultInfo'
import { VaultDisplayInfo } from 'utils/vaultDisplayInfo'
import { VaultNumberUpdate } from '../types'
import { useTokenPrice } from 'utils/useTokenPrice'
import { getFormattedUsdValue } from 'utils/usdFormatters'
import { useCurrentWalletAddress } from 'utils/useCurrentWalletAddress'

interface VaultRowProps {
  updateNumbers: (obj: VaultNumberUpdate) => void
  displayInfo: VaultDisplayInfo
  isLast: boolean
  isInitial: boolean
  isFrozen?: boolean
}

const IconNextContainer = styled.div`
  display: flex;
  position: absolute;
  right: 40px;
  cursor: pointer;
  @media screen and (max-width: ${p => p.theme.breakpoints.md}px) {
    display: none;
  }
`

const VaultRowWrapper = styled.div<{ $isLast?: boolean }>`
  position: relative;
  padding: 24px 32px;
  display: flex;
  align-items: center;
  gap: 20px;
  cursor: pointer;
  &:hover {
    background-color: ${p => p.theme.colors.background.active};
    ${p => p.$isLast && `border-radius: 0 0 32px 32px`};
    & > div > svg > rect {
      fill: ${p => p.theme.colors.button.background};
    }
    & > div > svg > path {
      stroke: ${p => p.theme.colors.background.primary};
    }
  }
`

const Td = styled.div`
  display: flex;
  align-items: center;
  @media screen and (min-width: ${p => p.theme.breakpoints.md}px) {
    flex-basis: 200px;
  }
`

const MobileRowHeader = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-bottom: 12px;
  border-bottom: ${p => `1px solid ${p.theme.colors.divider}`};
`

const MobileVaultRowWrapper = styled.div`
  padding: 12px;
  align-items: center;
  gap: 20px;
  cursor: pointer;
  border: ${p => `1px solid ${p.theme.colors.divider}`};
  border-radius: 16px;
  margin-bottom: 8px;
`

const MobileRowItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`

export const ColumnContentLoading = () => {
  const theme = useTheme()

  return (
    <ContentLoader
      speed={2}
      width={150}
      height={25}
      viewBox="0 0 150 25"
      backgroundColor={theme.colors.contentLoader.backgroundColor}
      foregroundColor={theme.colors.contentLoader.foregroundColor}
    >
      <rect x="0" y="8" rx="3" ry="3" width="75" height="7" />
      <rect x="0" y="26" rx="3" ry="3" width="150" height="7" />
    </ContentLoader>
  )
}

const MobileColumnContentLoading = () => {
  const theme = useTheme()

  return (
    <ContentLoader
      speed={2}
      width={75}
      height={25}
      viewBox="0 0 75 25"
      backgroundColor={theme.colors.contentLoader.backgroundColor}
      foregroundColor={theme.colors.contentLoader.foregroundColor}
    >
      <rect x="0" y="8" rx="3" ry="3" width="75" height="7" />
      <rect x="0" y="26" rx="3" ry="3" width="75" height="7" />
    </ContentLoader>
  )
}

export const VaultRow = ({ displayInfo, updateNumbers, isLast, isInitial, isFrozen }: VaultRowProps) => {
  const userInfo = useUserVaultInfo(displayInfo.vault)
  const vaultInfo = useVaultInfo(displayInfo.vault, { refetchInterval: 5000 })
  const { price: usdPrice } = useTokenPrice(displayInfo.vault.T, USDC)

  const walletAddress = useCurrentWalletAddress()
  const isConnected = !!walletAddress

  const deposited = userInfo.inventoryAtCost && displayInfo.formatAmount(userInfo.inventoryAtCost)

  const earned =
    userInfo.realizedProfit !== undefined &&
    userInfo.unrealizedProfit !== undefined &&
    displayInfo.formatAmount(userInfo.realizedProfit.toNumber() + userInfo.unrealizedProfit.toNumber())
  const estApy = vaultInfo.apy !== undefined ? round(vaultInfo.apy * 100, 2) + '%' : ''
  const tvl = vaultInfo.tvl && displayInfo.formatAmount(vaultInfo.tvl)

  const userInfoDepositedNum = !isConnected ? 0 : userInfo.inventoryAtCost?.toNumber()
  const userInfoEarnedNum = !isConnected
    ? 0
    : userInfo.realizedProfit !== undefined && userInfo.unrealizedProfit !== undefined
      ? userInfo.realizedProfit.toNumber() + userInfo.unrealizedProfit.toNumber()
      : undefined
  const tvlNum = vaultInfo.tvl?.toNumber()

  const tvlUsd =
    typeof usdPrice !== 'undefined' && typeof tvlNum !== 'undefined' ? tvlNum * usdPrice.human.toNumber() : undefined
  const earnedUsd =
    typeof usdPrice !== 'undefined' && typeof userInfoEarnedNum !== 'undefined'
      ? userInfoEarnedNum * usdPrice.human.toNumber()
      : undefined
  const depositedUsd =
    typeof usdPrice !== 'undefined' && typeof userInfoDepositedNum !== 'undefined'
      ? userInfoDepositedNum * usdPrice.human.toNumber()
      : undefined

  useEffect(() => {
    if (
      typeof userInfoDepositedNum !== 'number' ||
      typeof userInfoEarnedNum !== 'number' ||
      typeof tvlNum !== 'number' ||
      typeof vaultInfo.apy !== 'number'
    ) {
      return
    }
    updateNumbers({
      vaultId: displayInfo.vault.id,
      tvlUsd: tvlUsd,
      apy: vaultInfo.apy,
      earnedUsd: earnedUsd,
      depositedUsd: depositedUsd,
    })
  }, [
    displayInfo.vault.id,
    userInfoDepositedNum,
    userInfoEarnedNum,
    vaultInfo.apy,
    tvlNum,
    updateNumbers,
    tvlUsd,
    earnedUsd,
    depositedUsd,
  ])

  const isMobile = useIsMobile()

  const linkTo = isFrozen ? `/vaults/frozen/${displayInfo.tSymbol}` : `/vaults/${displayInfo.tSymbol}`

  return (
    <Link
      to={linkTo}
      key={displayInfo.tSymbol}
      style={{ textDecoration: 'none', pointerEvents: isInitial ? 'none' : 'auto' }}
    >
      {isMobile ? (
        <MobileVaultRowWrapper key={displayInfo.tSymbol}>
          {isInitial ? (
            <div style={{ height: 89 }} />
          ) : (
            <>
              <MobileRowHeader>
                <Td>
                  <displayInfo.icon />
                  <Typography variant="body" style={{ marginLeft: 15 }}>
                    {displayInfo.tSymbol}
                    {usdPrice && (
                      <Typography variant="caption" color="secondary">
                        {getFormattedUsdValue(usdPrice.human.toNumber(), { approx: false })}
                      </Typography>
                    )}
                  </Typography>
                </Td>
                <Td style={{ display: 'block' }}>
                  <Typography variant="body" color="secondary" style={{ textAlign: 'right' }}>
                    APY
                  </Typography>
                  <Typography variant="body" fontWeight="semiBold">
                    {estApy ? estApy : <ColumnContentLoading />}
                  </Typography>
                </Td>
              </MobileRowHeader>
              <MobileRowItem style={{ paddingTop: 12 }}>
                <Typography variant="body" color="secondary" style={{ textAlign: 'right' }}>
                  TVL
                </Typography>
                <Typography variant="body" style={{ textAlign: 'right' }}>
                  {tvl ? (
                    <>
                      {tvl}
                      {tvlUsd && (
                        <Typography variant="caption" color="secondary">
                          {getFormattedUsdValue(tvlUsd)}
                        </Typography>
                      )}
                    </>
                  ) : (
                    <MobileColumnContentLoading />
                  )}
                </Typography>
              </MobileRowItem>
              <MobileRowItem style={{ marginTop: 8 }}>
                <Typography variant="body" color="secondary" style={{ textAlign: 'right' }}>
                  Deposited
                </Typography>
                <Typography variant="body" style={{ textAlign: 'right' }}>
                  {!isConnected ? (
                    displayInfo.formatAmount(0)
                  ) : deposited ? (
                    <>
                      {deposited}
                      {typeof depositedUsd === 'number' && (
                        <Typography variant="caption" color="secondary">
                          {getFormattedUsdValue(depositedUsd)}
                        </Typography>
                      )}
                    </>
                  ) : (
                    <MobileColumnContentLoading />
                  )}
                </Typography>
              </MobileRowItem>
              <MobileRowItem style={{ marginTop: 8 }}>
                <Typography variant="body" color="secondary" style={{ textAlign: 'right' }}>
                  Earned
                </Typography>
                <Typography variant="body" style={{ textAlign: 'right' }}>
                  {!isConnected ? (
                    displayInfo.formatAmount(0)
                  ) : earned ? (
                    <>
                      {earned}
                      {typeof earnedUsd === 'number' && (
                        <Typography variant="caption" color="secondary">
                          {getFormattedUsdValue(earnedUsd)}
                        </Typography>
                      )}
                    </>
                  ) : (
                    <MobileColumnContentLoading />
                  )}
                </Typography>
              </MobileRowItem>
            </>
          )}
        </MobileVaultRowWrapper>
      ) : (
        <VaultRowWrapper key={displayInfo.tSymbol} $isLast={isLast}>
          <Td>
            {isInitial ? (
              <div style={{ height: 33 }} />
            ) : (
              <>
                <displayInfo.icon />
                <Typography variant="label1" style={{ marginLeft: 15 }}>
                  {displayInfo.tSymbol}
                  {usdPrice && (
                    <Typography variant="body" color="secondary">
                      {getFormattedUsdValue(usdPrice.human.toNumber(), { approx: false })}
                    </Typography>
                  )}
                </Typography>
              </>
            )}
          </Td>
          <Td style={{ flexBasis: 250 }}>
            {isInitial ? (
              <div style={{ height: 33 }} />
            ) : (
              <Typography variant="label1">
                {tvl ? (
                  <>
                    {tvl}
                    {tvlUsd && (
                      <Typography variant="body" color="secondary">
                        {getFormattedUsdValue(tvlUsd)}
                      </Typography>
                    )}
                  </>
                ) : (
                  <ColumnContentLoading />
                )}
              </Typography>
            )}
          </Td>
          <Td style={{ flexBasis: 150 }}>
            {isInitial ? (
              <div style={{ height: 33 }} />
            ) : (
              <Typography variant="label1">{estApy ? estApy : <ColumnContentLoading />}</Typography>
            )}
          </Td>
          <Td>
            {isInitial ? (
              <div style={{ height: 33 }} />
            ) : (
              <Typography variant="label1">
                {!isConnected ? (
                  displayInfo.formatAmount(0)
                ) : deposited ? (
                  <>
                    {deposited}
                    {typeof depositedUsd === 'number' && (
                      <Typography variant="body" color="secondary">
                        {getFormattedUsdValue(depositedUsd)}
                      </Typography>
                    )}
                  </>
                ) : (
                  <ColumnContentLoading />
                )}
              </Typography>
            )}
          </Td>
          <Td>
            {isInitial ? (
              <div style={{ height: 33 }} />
            ) : (
              <Typography variant="label1">
                {!isConnected ? (
                  displayInfo.formatAmount(0)
                ) : earned ? (
                  <>
                    {earned}
                    {typeof earnedUsd === 'number' && (
                      <Typography variant="body" color="secondary">
                        {getFormattedUsdValue(earnedUsd)}
                      </Typography>
                    )}
                  </>
                ) : (
                  <ColumnContentLoading />
                )}
              </Typography>
            )}
          </Td>
          {isInitial ? null : (
            <IconNextContainer>
              <IconButtonNext />
            </IconNextContainer>
          )}
        </VaultRowWrapper>
      )}
    </Link>
  )
}
